import React from 'react';

// Where a pinch came from: two fingers on a touch screen, or a trackpad, which browsers report as
// ctrl + wheel (Chrome, Firefox) or as gesture events (Safari).
export type PinchSource = 'touch' | 'trackpad';

interface PinchGestureHandlers {
  // return false to let the pinch pass, such as when there's nothing to zoom yet
  onPinchStart: (source: PinchSource) => boolean | void;
  // `ratio` is the scale since the pinch started; `x` and `y` are the client point it's centered on
  onPinch: (ratio: number, x: number, y: number, source: PinchSource) => void;
  onPinchEnd: () => void;
  // a one-finger touch drag, in client pixels since the last move
  onPan: (dx: number, dy: number) => void;
  onDoubleTap: (x: number, y: number) => void;
}

// Safari's trackpad and touch pinch events, which aren't in the DOM types
interface SafariGestureEvent extends UIEvent {
  scale: number;
  clientX: number;
  clientY: number;
}

// a trackpad pinch has no end event of its own, so it ends after the wheel goes quiet
const WHEEL_END_DELAY = 150;
// how much each wheel pixel scales by
const WHEEL_SENSITIVITY = 0.01;
const TAP_DISTANCE = 10;
const TAP_DURATION = 250;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 30;

/**
 * Reports pinches from fingers and trackpads, one-finger drags and double taps on an element, and keeps
 * the browser from zooming the page for them. It holds no zoom state: the caller decides what they do.
 */
export function usePinchGesture(element: HTMLElement | null, handlers: PinchGestureHandlers) {
  const handlersRef = React.useRef(handlers);
  handlersRef.current = handlers;

  React.useEffect(() => {
    if (!element) return undefined;

    const touches = new Map<number, { x: number; y: number }>();
    let touchPinch: { distance: number } | null = null;
    let tap: { x: number; y: number; time: number; pinched: boolean } | null = null;
    let lastTap: { x: number; y: number; time: number } | null = null;
    let wheelRatio = 0;
    let wheelTimer: ReturnType<typeof setTimeout> | undefined;
    let safariPinch = false;

    function twoTouches() {
      const [a, b] = Array.from(touches.values());
      return {
        distance: Math.hypot(a.x - b.x, a.y - b.y),
        x: (a.x + b.x) / 2,
        y: (a.y + b.y) / 2,
      };
    }

    function handlePointerDown(e: PointerEvent) {
      if (e.pointerType !== 'touch') return;

      // the first finger of a new touch starts the count over
      if (e.isPrimary) {
        touches.clear();
        if (touchPinch) {
          touchPinch = null;
          handlersRef.current.onPinchEnd();
        }
      }
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (touches.size === 1) {
        tap = { x: e.clientX, y: e.clientY, time: e.timeStamp, pinched: false };
        return;
      }

      if (tap) tap.pinched = true;
      if (touches.size !== 2 || touchPinch) return;
      if (handlersRef.current.onPinchStart('touch') === false) return;
      touchPinch = { distance: twoTouches().distance || 1 };
    }

    function handlePointerMove(e: PointerEvent) {
      const previous = touches.get(e.pointerId);
      if (e.pointerType !== 'touch' || !previous) return;
      touches.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (touchPinch && touches.size >= 2) {
        const { distance, x, y } = twoTouches();
        handlersRef.current.onPinch(distance / touchPinch.distance, x, y, 'touch');
        return;
      }

      if (touches.size === 1) handlersRef.current.onPan(e.clientX - previous.x, e.clientY - previous.y);
    }

    function handleTap(e: PointerEvent) {
      if (!tap || tap.pinched) return;
      const moved = Math.hypot(e.clientX - tap.x, e.clientY - tap.y);
      if (moved > TAP_DISTANCE || e.timeStamp - tap.time > TAP_DURATION) return;

      const isDouble = lastTap &&
        e.timeStamp - lastTap.time < DOUBLE_TAP_DELAY &&
        Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < DOUBLE_TAP_DISTANCE;

      if (isDouble) {
        lastTap = null;
        handlersRef.current.onDoubleTap(e.clientX, e.clientY);
        return;
      }

      lastTap = { x: e.clientX, y: e.clientY, time: e.timeStamp };
    }

    function handlePointerEnd(e: PointerEvent) {
      if (e.pointerType !== 'touch' || !touches.has(e.pointerId)) return;
      touches.delete(e.pointerId);

      if (e.type === 'pointerup' && touches.size === 0) handleTap(e);
      if (touches.size === 0) tap = null;

      if (touchPinch && touches.size < 2) {
        touchPinch = null;
        handlersRef.current.onPinchEnd();
      }
    }

    // iOS zooms the page for a two-finger touch unless its moves are cancelled, whatever the touch-action
    function handleTouchMove(e: TouchEvent) {
      if (e.touches.length > 1) e.preventDefault();
    }

    function handleWheel(e: WheelEvent) {
      if (!e.ctrlKey) return;
      e.preventDefault();

      if (!wheelRatio) {
        if (handlersRef.current.onPinchStart('trackpad') === false) return;
        wheelRatio = 1;
      }

      wheelRatio *= Math.exp(-e.deltaY * WHEEL_SENSITIVITY);
      handlersRef.current.onPinch(wheelRatio, e.clientX, e.clientY, 'trackpad');

      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        wheelRatio = 0;
        handlersRef.current.onPinchEnd();
      }, WHEEL_END_DELAY);
    }

    // Safari reports trackpad pinches, and touch ones on iOS, as gesture events. Touch pinches already
    // arrive as pointers, so these only zoom when no finger is down.
    function handleGestureStart(e: Event) {
      e.preventDefault();
      if (touches.size) return;
      safariPinch = handlersRef.current.onPinchStart('trackpad') !== false;
    }

    function handleGestureChange(e: Event) {
      e.preventDefault();
      if (!safariPinch) return;
      const { scale, clientX, clientY } = e as SafariGestureEvent;
      handlersRef.current.onPinch(scale, clientX, clientY, 'trackpad');
    }

    function handleGestureEnd(e: Event) {
      e.preventDefault();
      if (!safariPinch) return;
      safariPinch = false;
      handlersRef.current.onPinchEnd();
    }

    // A touch starts on the element, and its moves and ends are heard on the window: a finger lifted after
    // the element it touched has left the page, such as a preview swapped for its full-size image, never
    // reaches the element, and would otherwise count as down for good.
    element.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointermove', handlePointerMove, true);
    window.addEventListener('pointerup', handlePointerEnd, true);
    window.addEventListener('pointercancel', handlePointerEnd, true);
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    // Safari pinches with gesture events, so it doesn't need the wheel listener, which, being blocking,
    // can leave its two-finger swipes to its history navigation rather than the scroller underneath
    const listensToWheel = !('GestureEvent' in window);
    if (listensToWheel) element.addEventListener('wheel', handleWheel, { passive: false });
    element.addEventListener('gesturestart', handleGestureStart);
    element.addEventListener('gesturechange', handleGestureChange);
    element.addEventListener('gestureend', handleGestureEnd);

    return () => {
      clearTimeout(wheelTimer);
      element.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointermove', handlePointerMove, true);
      window.removeEventListener('pointerup', handlePointerEnd, true);
      window.removeEventListener('pointercancel', handlePointerEnd, true);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('wheel', handleWheel);
      element.removeEventListener('gesturestart', handleGestureStart);
      element.removeEventListener('gesturechange', handleGestureChange);
      element.removeEventListener('gestureend', handleGestureEnd);
    };
  }, [element]);
}
